import { Component, signal, inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { ChargingUser } from '../charging-user.model';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Department } from '../../department/department.model';
import { IdTagUserType } from 'src/app/shared/enums/id-tag-user-type.enum';
import { firstValueFrom, startWith, take, tap } from 'rxjs';
import { TopLoaderService } from 'src/app/core/services/top-loader.service';
import { UpdateChargingUserRequest } from './charging-user-edit.model';
import { ChargingUserService } from 'src/app/shared/services/charging-user.service';
import { SnackbarService } from 'ok-angular-shared/components';
import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { CountryCodeSelectorComponent } from 'src/app/shared/components/country-code-selector/country-code-selector.component';
import { PhoneNumberInputComponent } from 'src/app/shared/components/phone-number-input/phone-number-input.component';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatRadioModule } from '@angular/material/radio';
import { AdmDepTypeaheadComponent } from 'src/app/shared/components/adm-dep-typeahead/adm-dep-typeahead.component';
import { AdmG4AgreementNumberComponent } from 'src/app/shared/components/adm-g4-agreement-number/adm-g4-agreement-number.component';
import { AdmG4AccountNumberComponent } from 'src/app/shared/components/adm-g4-account-number/adm-g4-account-number.component';
import {
  AddressChanged,
  DepAddressTypeaheadComponent,
  InitialAddress,
} from 'src/app/shared/components/dep-address-typeahead/dep-address-typeahead.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Guid } from 'guid-typescript';
import { DepartmentChangeDialogComponent } from '../charging-user-department-change-dialog/charging-user-department-change-dialog.component';

export interface IEditChargingUserForm {
  department: FormControl<Department | null>;
  email: FormControl<string | null>;
  name: FormControl<string | null>;
  deliveryAddress: FormControl<string | null>;
  phoneNumber: FormControl<string>;
  countryCode: FormControl<number>;
  userHasCompanyCar: FormControl<boolean | null>;
  id: FormControl<string | null>;
  invoicingAccountNumber: FormControl<number | null>;
  agreementNumber: FormControl<number | null>;
  darId: FormControl<Guid | null>;
}

export interface ChargingUserEditModalData {
  chargingUserId: number;
}

@Component({
  selector: 'adm-charging-user-edit-modal',
  templateUrl: './charging-user-edit-modal.component.html',
  styleUrl: './charging-user-edit-modal.component.scss',
  imports: [
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatCheckboxModule,
    MatRadioModule,
    PhoneNumberInputComponent,
    CountryCodeSelectorComponent,
    PhoneNumberInputComponent,
    AdmDepTypeaheadComponent,
    AdmG4AgreementNumberComponent,
    AdmG4AccountNumberComponent,
    DepAddressTypeaheadComponent,
  ],
})
export class ChargingUserEditModalComponent {
  dialogRef = inject<MatDialogRef<ChargingUserEditModalComponent>>(MatDialogRef);
  private loadingService = inject(TopLoaderService);
  data = inject<ChargingUserEditModalData>(MAT_DIALOG_DATA);
  private chargingUserService = inject(ChargingUserService);
  private snackbarService = inject(SnackbarService);
  private dialog = inject(MatDialog);

  public userFormGroup: FormGroup<IEditChargingUserForm> = new FormGroup<IEditChargingUserForm>({
    department: new FormControl<Department | null>(null, [Validators.required]),
    email: new FormControl(null),
    name: new FormControl(null, Validators.required),
    deliveryAddress: new FormControl(null),
    phoneNumber: new FormControl<string>('', { validators: [Validators.required], nonNullable: true }),
    countryCode: new FormControl<number>(45, { nonNullable: true }),
    userHasCompanyCar: new FormControl(null),
    id: new FormControl(null),
    invoicingAccountNumber: new FormControl(null),
    agreementNumber: new FormControl(null),
    darId: new FormControl<Guid | null>(null),
  });

  public chargingUserUpdated: ChargingUser | null = null;
  public chargingUserHasDepartment = signal(false);
  public hasCompanyCar = signal<boolean | null>(false);
  public idTagUserType: typeof IdTagUserType = IdTagUserType;
  private initialAgreementNumber = signal<number | null>(null);
  resetDepartmentTrigger = signal<{ reset: boolean }>({ reset: false });
  initialAddress = signal<InitialAddress | null>(null);

  chargingUser = signal<ChargingUser | null>(null);

  constructor() {
    this.userFormGroup.controls.userHasCompanyCar.valueChanges
      .pipe(startWith(this.hasCompanyCar()), takeUntilDestroyed())
      .subscribe(value => {
        if (value === true) {
          this.userFormGroup.controls.invoicingAccountNumber.setValue(null);
        } else if (value === false) {
          this.userFormGroup.controls.agreementNumber.setValue(null);
        }
      });

    this.chargingUserService
      .getChargingUser(this.data.chargingUserId)
      .pipe(
        take(1),
        tap(result => {
          this.chargingUser.set(result);
          this.chargingUserHasDepartment.set(result.departmentId > 0);
          this.initialAgreementNumber.set(result.agreementNumber);
          this.hasCompanyCar.set(this.determineCompanyCarValue(result.idTagUserType));

          this.userFormGroup.patchValue({
            userHasCompanyCar: this.hasCompanyCar(),
            id: result.alias,
            invoicingAccountNumber: result.invoicingAccountNumber,
            agreementNumber: result.agreementNumber,
            phoneNumber: result.phoneNumber,
            countryCode: result.phoneCountryCode,
            deliveryAddress: result.idTagShippingAddress,
            email: result.emailAddress,
            name: result.name,
            darId: result.darId,
          });
          if (result.idTagUserType !== IdTagUserType.NotDetermined) {
            this.userFormGroup.controls.userHasCompanyCar.disable();
          }

          this.initialAddress.set({
            fullAddress: result.idTagShippingAddress,
            darId: result.darId,
          });
        }),
        takeUntilDestroyed()
      )
      .subscribe();
  }

  departmentSelected(department: Department | null) {
    this.userFormGroup.patchValue({
      department: department,
    });
  }

  public close() {
    this.dialogRef.close({ chargingUserUpdated: this.chargingUserUpdated });
  }

  private departmentChangedModalShown = false;

  public async save() {
    this.loadingService.setLoading(true);
    const chargingUser = this.chargingUser();
    const department = this.userFormGroup.controls.department.value;
    if (!chargingUser || !department) {
      this.loadingService.setLoading(false);
      return;
    }

    const model = new UpdateChargingUserRequest();
    const previousDepartmentId = chargingUser.departmentId;
    const currentDepartment = department;
    const currentAgreementNumber = this.userFormGroup.controls.agreementNumber.value;

    model.idTagShippingAddress = this.userFormGroup.controls.deliveryAddress.value ?? undefined;
    model.departmentId = department.id;
    model.emailAddress = this.userFormGroup.controls.email.value ?? '';
    model.idTagUserType = this.determineIdTagUserType(this.userFormGroup.controls.userHasCompanyCar.value);
    model.agreementNumber = this.userFormGroup.controls.agreementNumber.value ?? undefined;
    model.invoicingAccountNumber = this.userFormGroup.controls.invoicingAccountNumber.value ?? undefined;
    model.name = this.userFormGroup.controls.name.value ?? '';
    model.phoneNumber = this.userFormGroup.controls.phoneNumber.value ?? '';
    model.alias = this.userFormGroup.controls.id.value ?? '';
    model.phoneCountryCode = this.userFormGroup.controls.countryCode.value ?? 45;
    model.darId = this.userFormGroup.controls.darId.value?.toString() ?? '';

    if (
      previousDepartmentId !== currentDepartment?.id &&
      !this.departmentChangedModalShown &&
      currentAgreementNumber === this.initialAgreementNumber() &&
      currentAgreementNumber !== null
    ) {
      const dialogRef = this.dialog.open(DepartmentChangeDialogComponent);

      dialogRef.afterClosed().subscribe((result: boolean) => {
        if (result) {
          this.userFormGroup.controls.agreementNumber.reset();
          this.userFormGroup.controls.agreementNumber.markAsTouched();
          this.departmentChangedModalShown = true;
        } else {
          this.saveChargingUser(model);
        }
      });

      return;
    }

    this.saveChargingUser(model);
  }

  private saveChargingUser(model: UpdateChargingUserRequest) {
    const chargingUser = this.chargingUser();
    if (!chargingUser) {
      this.loadingService.setLoading(false);
      return;
    }

    this.chargingUserService.updateChargingUser(chargingUser.identifier, model).subscribe({
      next: async () => {
        this.loadingService.setLoading(false);
        const updatedUser = await firstValueFrom(this.chargingUserService.getChargingUser(chargingUser.id));
        this.dialogRef.close({ chargingUserUpdated: updatedUser });
        this.snackbarService.success(`Bruger opdateret`);
      },
      error: (httpError: HttpErrorResponse) => {
        if (httpError.status == HttpStatusCode.BadRequest) {
          switch (httpError.error?.code) {
            case 101100:
              this.snackbarService.error('Mobilnummeret angivet er ikke et gyldigt telefonnummer for den indtastede landekode.');
              break;
            case 101101:
              this.snackbarService.error('Mobilnummeret er af et ugyldigt format for den indtastede landekode.');
              break;
            default:
              this.snackbarService.error(httpError.error?.message);
              break;
          }
        } else {
          this.snackbarService.error(`Der er sket en ukendt fejl: (${httpError.error?.message})`);
        }
      },
    });
  }

  public resetDepartment() {
    this.userFormGroup.patchValue({
      department: null,
    });
  }

  public selectAddress(address: AddressChanged | null) {
    if (!address) {
      return;
    }

    this.userFormGroup.patchValue({ deliveryAddress: address.formatAddressToString ?? null, darId: address.darId ?? null });
  }

  private determineCompanyCarValue(idTagUserType: IdTagUserType) {
    switch (idTagUserType) {
      case IdTagUserType.CompanyCar:
        return true;
      case IdTagUserType.NoCompanyCar:
        return false;
      case IdTagUserType.NotDetermined:
        return null;
    }
  }

  private determineIdTagUserType(companyCar?: boolean | null) {
    if (companyCar === true) {
      return IdTagUserType.CompanyCar;
    } else if (companyCar === false) {
      return IdTagUserType.NoCompanyCar;
    } else {
      return IdTagUserType.NotDetermined;
    }
  }

  public getErrorMessage(controlName: string) {
    switch (controlName) {
      case 'id':
        return 'Indtast venlist et ID';
      case 'emailAddress':
        return 'Indtast venligst en valid email adresse';
      case 'deliveryAddress':
        return 'Vælg venligst en adresse';
      default:
        return '';
    }
  }
}
